/**
 * Copyright 2025 Amazon.com, Inc. and its affiliates. All Rights Reserved.
 *
 * Licensed under the Amazon Software License (the "License").
 * You may not use this file except in compliance with the License.
 * A copy of the License is located at
 *
 *   http://aws.amazon.com/asl/
 *
 * or in the "license" file accompanying this file. This file is distributed
 * on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either
 * express or implied. See the License for the specific language governing
 * permissions and limitations under the License.
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { CognitoAuth, getCognitoAuth, type UserInfo } from '../lib/cognito';

interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: UserInfo | null;
  accessToken: string | null;
  idToken: string | null;
  login: () => void;
  logout: () => void;
  checkAuth: () => Promise<boolean>;
  isTokenExpired: () => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: React.ReactNode;
  bypassAuth?: boolean; // For development mode
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children, bypassAuth = false }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [user, setUser] = useState<UserInfo | null>(null);
  const [cognitoAuth] = useState<CognitoAuth>(() => getCognitoAuth());

  // Check if token is expired
  const isTokenExpired = useCallback((): boolean => {
    if (bypassAuth) return false;
    return !cognitoAuth.isAuthenticated();
  }, [cognitoAuth, bypassAuth]);

  // Check authentication status
  const checkAuth = useCallback(async (): Promise<boolean> => {
    if (bypassAuth) {
      setIsAuthenticated(true);
      // Use mock user from Cognito auth (which reads from env vars)
      const mockUser = cognitoAuth.getUserInfo();
      setUser(mockUser);
      return true;
    }

    try {
      const authenticated = await cognitoAuth.handleAuth();
      setIsAuthenticated(authenticated);

      if (authenticated) {
        const userInfo = cognitoAuth.getUserInfo();
        setUser(userInfo);
      }

      return authenticated;
    } catch (error) {
      console.error('Authentication check failed:', error);
      setIsAuthenticated(false);
      setUser(null);
      return false;
    }
  }, [cognitoAuth, bypassAuth]);

  // Login - redirect to Cognito
  const login = useCallback(() => {
    if (bypassAuth) {
      setIsAuthenticated(true);
      return;
    }
    cognitoAuth.redirectToLogin();
  }, [cognitoAuth, bypassAuth]);

  // Logout - clear tokens and redirect
  const logout = useCallback(() => {
    if (bypassAuth) {
      setIsAuthenticated(false);
      setUser(null);
      return;
    }

    // Clear user state
    setIsAuthenticated(false);
    setUser(null);

    // Perform Cognito logout
    cognitoAuth.logout();
  }, [cognitoAuth, bypassAuth]);

  // Get access token
  const getAccessToken = useCallback((): string | null => {
    if (bypassAuth) return 'dev-token';
    return cognitoAuth.getAccessToken();
  }, [cognitoAuth, bypassAuth]);

  // Get ID token
  const getIdToken = useCallback((): string | null => {
    if (bypassAuth) return 'dev-id-token';
    return cognitoAuth.getIdToken();
  }, [cognitoAuth, bypassAuth]);

  // Check authentication on mount
  useEffect(() => {
    let isMounted = true;
    
    const initAuth = async () => {
      if (!isMounted) return;
      setIsLoading(true);
      await checkAuth();
      if (isMounted) {
        setIsLoading(false);
      }
    };

    initAuth();
    
    return () => {
      isMounted = false;
    };
  }, [checkAuth]);

  // Set up token expiration check
  useEffect(() => {
    if (!isAuthenticated || bypassAuth) return;

    // Check token expiration every minute
    const interval = setInterval(() => {
      if (isTokenExpired()) {
        console.warn('Token expired, logging out');
        logout();
      }
    }, 60000); // Check every minute

    return () => clearInterval(interval);
  }, [isAuthenticated, isTokenExpired, logout, bypassAuth]);

  const value: AuthContextType = {
    isAuthenticated,
    isLoading,
    user,
    accessToken: getAccessToken(),
    idToken: getIdToken(),
    login,
    logout,
    checkAuth,
    isTokenExpired,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Custom hook to use auth context
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
